import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Phone, Star, Quote, Check, Baby, Hotel, Church, PartyPopper, Moon, HandHelping, ShieldCheck } from "lucide-react";
import Seo from "@/components/Seo";
import ProofStrip from "@/components/ProofStrip";
import { Reveal, SectionHeading } from "@/components/Primitives";
import { WaveTop, WaveBottom, StarDoodle, DotsDoodle, Squiggle, BlobDoodle } from "@/components/Decor";
import { BUSINESS, IMAGES, SERVICES, TESTIMONIALS, HOW_IT_WORKS, PRICING } from "@/data/site";
import { trackEvent } from "@/lib/api";

const SERVICE_META = {
  babysitting: { icon: Baby, color: "#FFB703", bg: "#FFF3D1" },
  "hotel-care": { icon: Hotel, color: "#4CC9F0", bg: "#DFF6FE" },
  "church-care": { icon: Church, color: "#2A9D8F", bg: "#D7F0EC" },
  "special-events": { icon: PartyPopper, color: "#FF70A6", bg: "#FFE1EE" },
  "overnight-care": { icon: Moon, color: "#8B5CF6", bg: "#EDE6FE" },
  "pop-in-pa": { icon: HandHelping, color: "#06D6A0", bg: "#D4F7EC" },
};

function TestimonialCard({ t, testid }) {
  return (
    <figure
      className="w-[300px] md:w-[400px] shrink-0 bg-white/5 border border-white/10 rounded-[2rem] p-8"
      {...(testid ? { "data-testid": testid } : { "aria-hidden": true })}
    >
      <Quote className="w-8 h-8 text-gold mb-4" />
      <blockquote className="text-base md:text-lg leading-relaxed text-parchment/90 font-medium min-h-[120px]">"{t.quote}"</blockquote>
      <figcaption className="mt-5 text-sm flex items-center gap-3">
        <span className="w-10 h-10 rounded-full bg-coral/80 text-white flex items-center justify-center font-bold">{t.name.charAt(0)}</span>
        <span><span className="font-bold text-white block">{t.name}</span><span className="text-parchment/60">{t.location}</span></span>
      </figcaption>
    </figure>
  );
}

function TestimonialCarousel() {
  const reduce = useReducedMotion();
  return (
    <div className="relative group" data-testid="testimonial-carousel">
      <div className="pointer-events-none absolute inset-y-0 left-0 w-12 md:w-40 bg-gradient-to-r from-espresso to-transparent z-10" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-12 md:w-40 bg-gradient-to-l from-espresso to-transparent z-10" />
      <div className="overflow-hidden py-2">
        <div className={`flex w-max ${reduce ? "" : "animate-marquee"} group-hover:[animation-play-state:paused]`}>
          <div className="flex gap-6 pr-6">
            {TESTIMONIALS.map((t, i) => <TestimonialCard key={i} t={t} testid={`home-testimonial-${i}`} />)}
          </div>
          <div className="flex gap-6 pr-6" aria-hidden="true">
            {TESTIMONIALS.map((t, i) => <TestimonialCard key={`dup-${i}`} t={t} />)}
          </div>
        </div>
      </div>
      <p className="text-center text-xs text-parchment/50 mt-6">Hover to pause · {TESTIMONIALS.length} verified family reviews</p>
    </div>
  );
}

export default function Home() {
  const reduce = useReducedMotion();

  return (
    <>
      <Seo
        description="Mom's Best Friend Sitters — DFW's most trusted premium sitter agency since 1994. 11-step screened, CPR-certified caregivers for babysitting, hotel care, special events and overnight care."
        jsonLd={{ "@context": "https://schema.org", "@type": "Service", serviceType: "Childcare and babysitting agency", provider: { "@type": "Organization", name: BUSINESS.name }, areaServed: "Dallas–Fort Worth, TX" }}
      />

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#FFFDF7] via-[#FFF5DC] to-[#FFE9CC]">
        <StarDoodle className="absolute top-24 left-[4%] w-9 h-9 animate-float-slow hidden md:block" color="#FFB703" />
        <DotsDoodle className="absolute top-40 right-[45%] hidden lg:block" color="#4CC9F0" />
        <div className="container-mbf pt-14 md:pt-20 pb-24 grid lg:grid-cols-12 gap-10 lg:gap-8 items-center relative">
          <div className="lg:col-span-6">
            <motion.div initial={reduce ? false : { opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
              <span className="inline-flex items-center gap-2 bg-white border-2 border-[#F1E6D0] px-4 py-2 rounded-full text-xs font-bold text-espresso mb-6 shadow-soft" data-testid="hero-badge">
                <Star className="w-4 h-4 text-gold fill-gold" /> DFW's #1 Rated Sitter Agency Since {BUSINESS.established}
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-espresso">
                Safe, loving care for your{" "}
                <span className="relative inline-block text-coral">little ones
                  <Squiggle className="absolute -bottom-2 left-0 w-full h-3" color="#FFB703" />
                </span>
              </h1>
              <p className="mt-7 text-lg text-stoneish leading-relaxed max-w-xl font-medium">
                Warm, 11-step screened, CPR-certified caregivers across Dallas–Fort Worth — matched to your family by a real person, not an app.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to="/book" onClick={() => trackEvent("cta_click", { source: "hero" })} className="btn-accent text-base !px-8 !py-4" data-testid="hero-request-cta">Book a Sitter Now <ArrowRight className="w-4 h-4" /></Link>
                <Link to="/safety-screening" className="btn-outline text-base !px-8 !py-4" data-testid="hero-services-cta"><ShieldCheck className="w-4 h-4" /> Our 11-Step Safety</Link>
              </div>
              <div className="mt-7 flex items-center gap-4 text-sm text-stoneish">
                <a href={BUSINESS.phoneHref} onClick={() => trackEvent("call_click", { source: "hero" })} className="flex items-center gap-2 font-bold text-espresso hover:text-coral transition-colors" data-testid="hero-phone-link"><Phone className="w-4 h-4 text-gold" /> {BUSINESS.phone}</a>
                <span className="h-4 w-px bg-[#E2D7C3]" />
                <span className="font-medium">4-hour minimum · Booked in minutes</span>
              </div>
            </motion.div>
          </div>

          <div className="lg:col-span-6">
            <motion.div className="relative" initial={reduce ? false : { opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}>
              <BlobDoodle className="absolute -top-8 -right-6 w-40 h-40 -z-0" color="#4CC9F0" />
              <div className="relative rounded-[2.5rem] overflow-hidden border-[6px] border-white shadow-pop ring-4 ring-gold/30">
                <img src={IMAGES.hero} alt="A caregiver joyfully lifting a happy child" className="w-full h-[380px] md:h-[520px] object-cover" />
              </div>
              <div className="absolute -bottom-6 left-2 sm:-left-6 bg-white rounded-[1.5rem] shadow-pop border-2 border-[#F1E6D0] p-5 max-w-[230px]" data-testid="hero-floating-card">
                <div className="flex items-center gap-1 mb-1.5">{[...Array(5)].map((_, i) => <Star key={i} className="w-4 h-4 text-gold fill-gold" />)}</div>
                <p className="text-sm text-stoneish leading-snug font-medium">"I've LOVED every sitter they sent us — they play with and encourage my little one."</p>
                <p className="text-xs font-bold text-espresso mt-2">— Kara D., Southlake</p>
              </div>
              <div className="absolute -top-3 -left-3 bg-mint text-white w-20 h-20 rounded-full flex flex-col items-center justify-center shadow-teal animate-float-slow">
                <span className="font-display font-bold text-2xl leading-none">30+</span>
                <span className="text-[9px] font-bold uppercase tracking-wide">Years</span>
              </div>
            </motion.div>
          </div>
        </div>
        <WaveBottom color="#FFFFFF" />
      </section>

      <ProofStrip />

      {/* SERVICES */}
      <section className="container-mbf py-20 md:py-28 relative">
        <StarDoodle className="absolute top-16 right-[3%] w-7 h-7 hidden md:block" color="#FF70A6" />
        <SectionHeading center overline="Our Services" title="Tailored care, big smiles, lifelong trust" subtitle="Six dedicated services — each staffed by 11-step screened, CPR-certified caregivers who treat your family like their own." />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s, i) => {
            const m = SERVICE_META[s.id];
            return (
              <Reveal key={s.id} delay={i * 0.06}>
                <Link to={`/services/${s.id}`} className="card-warm p-7 flex flex-col h-full group" data-testid={`home-service-${s.id}`}>
                  <span className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5" style={{ backgroundColor: m.bg }}>
                    <m.icon className="w-7 h-7" style={{ color: m.color }} />
                  </span>
                  <h3 className="text-xl font-bold text-espresso mb-2 group-hover:text-coral transition-colors">{s.name}</h3>
                  <p className="text-sm text-stoneish leading-relaxed flex-1 font-medium">{s.short}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold" style={{ color: m.color }}>Know more <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" /></span>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* STATS / ABOUT */}
      <section className="relative bg-parchment-2">
        <WaveTop color="#FAF4E8" />
        <div className="container-mbf py-20 md:py-28 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="relative">
              <BlobDoodle className="absolute -bottom-8 -left-8 w-40 h-40" color="#FFB703" />
              <div className="rounded-[2.5rem] overflow-hidden border-[6px] border-white shadow-pop ring-4 ring-teal/20">
                <img src={IMAGES.sitterReading} alt="A caregiver playing blocks with a child" className="w-full h-[440px] object-cover" />
              </div>
              <div className="absolute -top-5 right-6 bg-white rounded-2xl shadow-soft border-2 border-[#F1E6D0] px-5 py-3 flex items-center gap-3">
                <span className="font-display font-bold text-3xl text-coral">10k+</span>
                <span className="text-xs font-bold text-stoneish leading-tight">Happy DFW<br />families</span>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="overline mb-3"><Squiggle className="w-8 h-3" color="#2A9D8F" /> Why families love us</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-espresso leading-tight mb-5">Committed to your child's safety & happiness</h2>
            <p className="text-lg text-stoneish leading-relaxed mb-6 font-medium">Every caregiver clears our rigorous 11-step screening — criminal & sex offender registry checks, motor vehicle and education verification, CPR & First Aid training and a 7-year social media screening. We only place the caregivers we'd trust with our own children.</p>
            <ul className="space-y-3 mb-8">
              {["Criminal & sex offender registry checks", "Motor vehicle & education verification", "CPR & First Aid trained", "7-year social media screening"].map((item) => (
                <li key={item} className="flex items-center gap-3 text-base text-espresso font-semibold">
                  <span className="w-6 h-6 rounded-full bg-mint/20 text-mint flex items-center justify-center shrink-0"><Check className="w-3.5 h-3.5" /></span>{item}
                </li>
              ))}
            </ul>
            <Link to="/safety-screening" className="btn-teal" data-testid="home-screening-cta">See the 11-step screening <ArrowRight className="w-4 h-4" /></Link>
          </Reveal>
        </div>
        <WaveBottom color="#FAF4E8" />
      </section>

      {/* HOW IT WORKS */}
      <section className="container-mbf py-20 md:py-28">
        <SectionHeading center overline="Simple Steps" title="A smooth process to book trusted care" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {HOW_IT_WORKS.map((step, i) => {
            const colors = ["#FFB703", "#FF70A6", "#2A9D8F", "#4CC9F0"];
            return (
              <Reveal key={step.n} delay={i * 0.06}>
                <div className="card-warm p-7 h-full relative" data-testid={`home-step-${step.n}`}>
                  <div className="w-12 h-12 rounded-2xl text-white font-display font-bold text-lg flex items-center justify-center mb-4" style={{ backgroundColor: colors[i] }}>{step.n}</div>
                  <h4 className="text-lg font-bold text-espresso mb-2">{step.title}</h4>
                  <p className="text-sm text-stoneish leading-relaxed font-medium">{step.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
        <div className="text-center mt-12"><Link to="/how-it-works" className="btn-outline" data-testid="home-howitworks-cta">See the full process</Link></div>
      </section>

      {/* TESTIMONIALS */}
      <section className="relative bg-espresso text-parchment">
        <WaveTop color="#1E293B" />
        <div className="container-mbf py-20 md:py-28">
          <div className="max-w-2xl mb-12 mx-auto text-center">
            <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.14em] font-bold text-gold mb-3 justify-center">Client Testimonials</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white leading-tight">Loved by DFW families for generations</h2>
          </div>
          <TestimonialCarousel />
          <div className="text-center mt-12"><Link to="/reviews" className="inline-flex items-center gap-2 text-gold font-bold hover:text-white transition-colors" data-testid="home-reviews-cta">Read more reviews <ArrowRight className="w-4 h-4" /></Link></div>
        </div>
        <WaveBottom color="#1E293B" />
      </section>

      {/* PRICING SNAPSHOT */}
      <section className="container-mbf py-20 md:py-28">
        <SectionHeading center overline="Memberships" title="Simple, transparent pricing" subtitle="Pricing shown for reference — our team confirms every detail with you personally." />
        <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {PRICING.map((tier) => (
            <div key={tier.id} className={`rounded-[2rem] p-8 border-2 ${tier.featured ? "bg-espresso text-parchment border-espresso shadow-pop relative overflow-hidden" : "bg-white border-[#F1E6D0] shadow-soft"}`} data-testid={`home-pricing-${tier.id}`}>
              {tier.featured && <span className="absolute top-5 right-5 text-xs uppercase tracking-widest text-espresso font-bold bg-gold px-3 py-1 rounded-full">Popular</span>}
              <h3 className={`font-display font-bold text-2xl ${tier.featured ? "text-white" : "text-espresso"}`}>{tier.name}</h3>
              <div className="mt-3 mb-4"><span className="font-display font-bold text-5xl">{tier.price}</span><span className={tier.featured ? "text-parchment/70" : "text-stoneish"}>{tier.period}</span></div>
              <p className={`text-sm mb-5 font-medium ${tier.featured ? "text-parchment/80" : "text-stoneish"}`}>{tier.desc}</p>
              <Link to="/pricing" className={tier.featured ? "btn-primary w-full" : "btn-outline w-full"} data-testid={`home-pricing-cta-${tier.id}`}>View details</Link>
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="container-mbf pb-24">
        <div className="rounded-[2.5rem] bg-gradient-to-br from-[#FFB703] to-[#FF8A65] p-10 md:p-16 text-center relative overflow-hidden">
          <StarDoodle className="absolute top-8 left-[10%] w-10 h-10 animate-wiggle" color="#FFFFFF" />
          <DotsDoodle className="absolute bottom-6 right-[8%]" color="#FFFFFF" />
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white leading-tight max-w-2xl mx-auto">Ready to give your family the best?</h2>
          <p className="mt-5 text-lg text-white/90 max-w-xl mx-auto font-medium">Tell us what you need — we'll match a screened sitter and confirm the details personally.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/book" onClick={() => trackEvent("cta_click", { source: "footer_cta" })} className="inline-flex items-center justify-center gap-2 bg-white text-espresso px-8 py-4 rounded-full font-bold text-base hover:-translate-y-0.5 transition-all shadow-lg" data-testid="home-final-request-cta">Request a Sitter <ArrowRight className="w-4 h-4" /></Link>
            <a href={BUSINESS.phoneHref} className="inline-flex items-center justify-center gap-2 bg-espresso text-white px-8 py-4 rounded-full font-bold text-base hover:-translate-y-0.5 transition-all" data-testid="home-final-phone-link"><Phone className="w-4 h-4" /> {BUSINESS.phone}</a>
          </div>
        </div>
      </section>
    </>
  );
}
